/** Logical canvas for Mad Monday balloon booth (authoring space). */
export const BALLOON_BOOTH_LOGICAL_WIDTH = 1920;
export const BALLOON_BOOTH_LOGICAL_HEIGHT = 1080;

export const BALLOON_BOOTH_BACKGROUND_SRC = '/minigames/balloon_booth.jpg';
export const BALLOON_BOOTH_SQUEAK_SOUND_SRC = '/minigames/balloon_squeak.mp3';
export const BALLOON_BOOTH_POP_SOUND_SRC = '/minigames/balloon_pop.mp3';
export const BALLOON_BOOTH_GAS_IMAGE_SRC = '/images/venom_cloud_target.png';
export const BALLOON_BOOTH_GAS_SOUND_SRC = '/sounds/venom_cloud.mp3';

/** Server `minigames.balloonBoothPop.balloonCount` */
export const BALLOON_BOOTH_BALLOON_COUNT = 20;
/** Server `minigames.balloonBoothPop.maxPops` */
export const BALLOON_BOOTH_MAX_POPS = 3;
/** Server `minigames.balloonBoothPop.completionPickValue` */
export const BALLOON_BOOTH_COMPLETION_PICK_VALUE = 3;
/** Spawn region: centered inner fraction of logical canvas (server `getBalloonBoothPlacementBounds`). */
export const BALLOON_BOOTH_PLACEMENT_INNER_FRACTION = 0.3;
/** Extra horizontal reach beyond the inner band (each side; must match server). */
export const BALLOON_BOOTH_PLACEMENT_HORIZONTAL_EXTEND_PX = 100;

/**
 * @returns {{ xMin: number, xMax: number, yMin: number, yMax: number }}
 */
export function getBalloonBoothPlacementBounds() {
  const innerW = BALLOON_BOOTH_LOGICAL_WIDTH * BALLOON_BOOTH_PLACEMENT_INNER_FRACTION;
  const innerH = BALLOON_BOOTH_LOGICAL_HEIGHT * BALLOON_BOOTH_PLACEMENT_INNER_FRACTION;
  const xMin = Math.round((BALLOON_BOOTH_LOGICAL_WIDTH - innerW) / 2) - BALLOON_BOOTH_PLACEMENT_HORIZONTAL_EXTEND_PX;
  const xMax = Math.round((BALLOON_BOOTH_LOGICAL_WIDTH + innerW) / 2) + BALLOON_BOOTH_PLACEMENT_HORIZONTAL_EXTEND_PX;
  return {
    xMin: Math.max(0, xMin),
    xMax: Math.min(BALLOON_BOOTH_LOGICAL_WIDTH, xMax),
    yMin: Math.round((BALLOON_BOOTH_LOGICAL_HEIGHT - innerH) / 2),
    yMax: Math.round((BALLOON_BOOTH_LOGICAL_HEIGHT + innerH) / 2),
  };
}

export const BALLOON_BOOTH_POP_EFFECT_MS = 900;
export const BALLOON_BOOTH_GAS_FADE_MS = 720;

const BALLOON_IMAGE_BY_COLOR = Object.freeze({
  blue: '/minigames/blue_balloon.png',
  green: '/minigames/green_balloon.png',
  red: '/minigames/red_balloon.png',
  yellow: '/minigames/yellow_balloon.png',
});

/**
 * @param {string} color
 * @returns {string}
 */
export function getBalloonBoothBalloonImageSrc(color) {
  if (typeof color !== 'string' || !color) {
    throw new Error(`getBalloonBoothBalloonImageSrc: invalid color (${JSON.stringify(color)})`);
  }
  const src = BALLOON_IMAGE_BY_COLOR[color];
  if (!src) {
    throw new Error(`getBalloonBoothBalloonImageSrc: unknown color ${JSON.stringify(color)}`);
  }
  return src;
}

/**
 * @param {number} logicalX
 * @param {number} logicalY
 * @returns {{ left: string, top: string }}
 */
export function balloonBoothLogicalToPercentPosition(logicalX, logicalY) {
  if (!Number.isFinite(logicalX) || !Number.isFinite(logicalY)) {
    throw new Error(`balloonBoothLogicalToPercentPosition: invalid coords (${logicalX}, ${logicalY})`);
  }
  return {
    left: `${(logicalX / BALLOON_BOOTH_LOGICAL_WIDTH) * 100}%`,
    top: `${(logicalY / BALLOON_BOOTH_LOGICAL_HEIGHT) * 100}%`,
  };
}

/** Balloon buttons anchor at the knot (bottom); pop FX sit at the visual center. */
export const BALLOON_BOOTH_EFFECT_OFFSET_LOGICAL_Y = 68;

/**
 * @param {number} logicalX
 * @param {number} logicalY
 * @returns {{ left: string, top: string }}
 */
export function balloonBoothLogicalToEffectPercentPosition(logicalX, logicalY) {
  if (!Number.isFinite(logicalX) || !Number.isFinite(logicalY)) {
    throw new Error(`balloonBoothLogicalToEffectPercentPosition: invalid coords (${logicalX}, ${logicalY})`);
  }
  const effectY = logicalY - BALLOON_BOOTH_EFFECT_OFFSET_LOGICAL_Y;
  return {
    left: `${(logicalX / BALLOON_BOOTH_LOGICAL_WIDTH) * 100}%`,
    top: `${(effectY / BALLOON_BOOTH_LOGICAL_HEIGHT) * 100}%`,
  };
}
